import { useMemo } from 'react';
import { Dropdown } from '../Common/Dropdown';
import { getOtherPaymentMethods } from '@/utils/paymentMethods';
import { PaymentMethodRow } from '../PaymentMethodRow/PaymentMethodRow';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useContext } from 'react';
import { PaymentMethodEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import { PaymentMethodRenderer } from '../PaymentMethodRenderer/PaymentMethodRenderer';
import { DrawerScreen } from '@/types/screens';
import { useScreenSurface } from '@/hooks/useScreenSurface';
import { useIsMobile } from '@/hooks/useIsMobile';
import { trackAndNotifyPaymentMethodSelected } from '@/utils/paymentClickEvents';
import type { ICheckoutContext } from '@/types/context';

export const OtherPaymentForm = () => {
  const context = useContext(CheckoutContext) as ICheckoutContext;
  const {
    paymentMethodsAvailable,
    otherMethodSelected,
    actions: { updateOtherMethodSelected },
  } = context;
  const { openScreen } = useScreenSurface();
  // On a phone the select opens the FULL method screen — the same one the
  // "Other" tile opens — rather than expanding a cramped inline list under the
  // field; `openScreen` puts it on whichever surface the template uses (drawer
  // for DEFAULT, in place for embedded view). Desktop has the room, so there the
  // Dropdown keeps expanding in place.
  const isMobile = useIsMobile();
  const otherPaymentMethods = useMemo(() => getOtherPaymentMethods(paymentMethodsAvailable), [paymentMethodsAvailable]);

  return (
    <div className="flex flex-col gap-4">
      <Dropdown
        options={otherPaymentMethods.map((pm) => ({
          element: <PaymentMethodRow paymentMethod={pm.paymentMethodName as PaymentMethodEnum} />,
          value: pm.paymentMethodName,
        }))}
        onSelect={({ value }) => {
          trackAndNotifyPaymentMethodSelected(context, value as ExcludeOtherPaymentMethod);
          updateOtherMethodSelected(value as ExcludeOtherPaymentMethod);
        }}
        selectedOption={{
          element: <PaymentMethodRow paymentMethod={otherMethodSelected as ExcludeOtherPaymentMethod} />,
          value: otherMethodSelected as ExcludeOtherPaymentMethod,
        }}
        onClick={isMobile ? () => openScreen(DrawerScreen.OTHER_PAYMENT_METHOD) : undefined}
      />
      {/* Keyed on the selected APM so only the inner form remounts/animates on
          switch — the dropdown above stays mounted so its logo <img>s aren't
          re-fetched. */}
      <div key={otherMethodSelected} className="checkout-payment-form-transition">
        <PaymentMethodRenderer paymentMethod={otherMethodSelected as ExcludeOtherPaymentMethod} />
      </div>
    </div>
  );
};
